import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { CardEmbed } from '@molt/shared';
import {
  AvatarStack,
  EmptyState,
  LabelChip,
  MarkdownBody,
  Notice,
  Skeleton,
} from '@/components';
import { cardEmbedQueryOptions } from '@/lib/card-share';
import { useInterfaceLanguage } from '@/lib/interface-language';

export const Route = createFileRoute('/embed/cards/$cardId')({
  staticData: { layout: 'embedded' },
  component: CardEmbedRoute,
});

function CardEmbedRoute() {
  const { t } = useInterfaceLanguage();
  const { cardId } = Route.useParams();
  const embed = useQuery(cardEmbedQueryOptions(cardId));

  if (embed.isPending) {
    return (
      <div className="flex min-h-screen flex-col gap-3 bg-surface p-4 text-ink">
        <Skeleton shape="line" width="66%" />
        <Skeleton shape="line" width="33%" />
        <Skeleton shape="block" />
      </div>
    );
  }

  if (embed.isError || embed.data === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface p-4 text-ink">
        <EmptyState
          tone="error"
          size="panel"
          title={t('boardDashboard.embed.errorTitle')}
          body={t('boardDashboard.embed.errorBody')}
        />
      </div>
    );
  }

  return <CardEmbedView embed={embed.data} />;
}

export function CardEmbedView({ embed }: { readonly embed: CardEmbed }) {
  const { t } = useInterfaceLanguage();
  const { card } = embed;
  const hasDates = card.startAt !== null || card.dueAt !== null;

  return (
    <article className="flex min-h-screen flex-col gap-4 bg-surface p-4 text-ink">
      <header className="flex flex-col gap-2">
        <p className="text-xs font-medium uppercase text-ink-muted">
          {t('boardDashboard.embed.eyebrow')}
        </p>
        <h1 className="break-words font-display text-2xl text-ink">{card.title}</h1>
        <p className="text-sm text-ink-muted">
          {t('boardDashboard.embed.inList', { list: card.list.title })}
        </p>
      </header>

      {card.archived ? (
        <Notice
          tone="warn"
          title={t('boardDashboard.embed.archivedTitle')}
          body={t('boardDashboard.embed.archivedBody')}
        />
      ) : null}

      {embed.labels.length === 0 ? null : (
        <section
          aria-label={t('boardDashboard.embed.labels')}
          className="flex flex-wrap gap-2"
        >
          {embed.labels.map((label) => (
            <LabelChip key={label.id} colour={label.colour} name={label.name} size="sm" />
          ))}
        </section>
      )}

      {embed.members.length === 0 ? null : (
        <section
          aria-label={t('boardDashboard.embed.members')}
          className="flex items-center gap-2"
        >
          <AvatarStack members={embed.members} max={6} size="sm" />
          <span className="text-sm">
            {embed.members.map((member) => member.displayName).join(', ')}
          </span>
        </section>
      )}

      {hasDates ? (
        <dl className="grid gap-1 text-sm">
          {card.startAt === null ? null : (
            <EmbedDate label={t('boardDashboard.embed.start')} value={card.startAt} />
          )}
          {card.dueAt === null ? null : (
            <EmbedDate label={t('boardDashboard.embed.due')} value={card.dueAt} />
          )}
          <div className="flex gap-2">
            <dt className="font-medium">{t('boardDashboard.embed.completed')}</dt>
            <dd>
              {card.completed
                ? t('boardDashboard.embed.yes')
                : t('boardDashboard.embed.no')}
            </dd>
          </div>
        </dl>
      ) : null}

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase text-ink-muted">
          {t('boardDashboard.embed.description')}
        </h2>
        {card.description === null ? (
          <p className="text-sm text-ink-muted">
            {t('boardDashboard.embed.noDescription')}
          </p>
        ) : (
          <MarkdownBody markdown={card.description} variant="description" />
        )}
      </section>

      {embed.checklists.length === 0 ? null : (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold uppercase text-ink-muted">
            {t('boardDashboard.embed.checklists')}
          </h2>
          <div className="grid gap-3">
            {embed.checklists.map((checklist) => (
              <section
                key={checklist.id}
                className="rounded-card border border-border p-3"
              >
                <h3 className="font-medium">{checklist.title}</h3>
                <ul className="mt-2 grid gap-1 text-sm">
                  {checklist.items.map((item) => (
                    <li key={item.id}>
                      {item.checked ? '[x]' : '[ ]'} {item.text}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </section>
      )}

      {embed.attachments.length === 0 ? null : (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold uppercase text-ink-muted">
            {t('boardDashboard.embed.attachments')}
          </h2>
          <ul className="grid gap-1 text-sm">
            {embed.attachments.map((attachment) => (
              <li key={attachment.id}>{attachment.displayName}</li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

function EmbedDate({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="font-medium">{label}</dt>
      <dd>
        <time dateTime={value}>{value}</time>
      </dd>
    </div>
  );
}
